import type { ARCPattern, ARCPatternResult, LiveSnapshot } from "@/arc/arc.types";

export const revenueAtRiskPattern: ARCPattern = {
  id: "revenue_at_risk",
  label: "Revenue at Risk",
  owner: "ops",
  urgency: "high",

  evaluate(snapshot: LiveSnapshot): ARCPatternResult {
    const { jobs, issues } = snapshot;
    const openJobs = jobs.filter((j) => j.currentStage !== "Completed");
    const openIssueJobIds = new Set(
      issues.filter((i) => i.status === "Open" || i.status === "In Progress").map((i) => i.jobId)
    );

    const jobRev = (j: typeof jobs[0]) =>
      j.financials?.contractValue ?? j.payoutAmount ?? 0;

    const atRisk = openJobs.filter((j) => {
      const materialIssue =
        j.materialReadiness === "Missing Items" ||
        j.materialReadiness === "Not Ordered" ||
        j.materialReadiness === "Field Purchase Required";
      const permitIssue =
        j.permitRequired &&
        j.permitStatus !== "Approved" &&
        j.permitStatus !== "Not Required";
      const hasOpenIssue = openIssueJobIds.has(j.id);
      const paymentIssue =
        j.financials &&
        j.financials.paymentStatus !== "Paid" &&
        (j.financials.paymentStatus === "Overdue" || j.financials.paymentStatus === "Invoiced");
      return materialIssue || permitIssue || hasOpenIssue || !!paymentIssue;
    });

    const amountAtRisk = atRisk.reduce((s, j) => s + jobRev(j), 0);

    return {
      triggered: atRisk.length > 0,
      count: atRisk.length,
      affectedJobIds: atRisk.map((j) => j.id),
      amountAtRisk,
      context:
        atRisk.length > 0
          ? `${atRisk.length} open job${atRisk.length > 1 ? "s" : ""} with revenue exposure — $${amountAtRisk.toLocaleString()} at risk`
          : "No revenue exposure detected",
      suggestedAction: "Review all flagged jobs — prioritize by contract value and install date proximity",
      snapshot: {
        atRiskJobIds: atRisk.map((j) => j.id),
        amountAtRisk,
        breakdown: atRisk.map((j) => ({
          id: j.id,
          customer: j.customerName,
          value: jobRev(j),
          stage: j.currentStage,
        })),
      },
    };
  },
};
